import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {ParametreDbSection} from '../../parametre-db/parametre-db.component';
import {ParametreDonneesStateService} from '../parametre-donnees-state.service';

export type DonneesTabId =
  | 'general'
  | 'champs'
  | 'affichage'
  | 'regles'
  | 'utilisation'
  | 'import'
  | 'export'
  | 'api'
  | 'historique';

/** État local (API `/data-settings/...` à brancher plus tard). */
export interface DonneesGeneralSettings {
  champsPersonnalisesActifs: boolean;
  groupesActifs: boolean;
  tagsActifs: boolean;
  variantesActives: boolean;
  maxChampsPersonnalises: number;
  historisationModifications: boolean;
  filtresAvances: boolean;
}

/** Sections param-tables connues (écran DB legacy) ; les autres modules utilisent un repli pour le champ réservé. */
const ROUTE_TO_DB_SECTION: Partial<Record<string, ParametreDbSection>> = {
  produits: 'produit',
  clients: 'client',
  fournisseurs: 'fournisseur',
  stock: 'stock'
};

const VALID_MODULE_KEYS = [
  'produits',
  'clients',
  'fournisseurs',
  'documents-vente',
  'documents-achat',
  'lignes-document',
  'promotions',
  'tarifs',
  'stock',
  'entrepots',
  'boutiques'
] as const;

const TABS_CUSTOM_DEPENDENT: DonneesTabId[] = [
  'champs',
  'affichage',
  'regles',
  'utilisation',
  'import',
  'export',
  'api'
];

@Component({
  selector: 'app-parametre-donnees-module',
  templateUrl: './parametre-donnees-module.component.html',
  styleUrls: ['./parametre-donnees-module.component.scss']
})
export class ParametreDonneesModuleComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  moduleKey = 'produits';
  moduleLabel = 'Produits';
  dbSection: ParametreDbSection = 'produit';

  activeTab: DonneesTabId = 'general';

  /** Tant que vrai, les données module ne sont pas encore chargées depuis l’API. */
  loadingModule = true;

  general: DonneesGeneralSettings = {
    champsPersonnalisesActifs: true,
    groupesActifs: true,
    tagsActifs: true,
    variantesActives: true,
    maxChampsPersonnalises: 50,
    historisationModifications: false,
    filtresAvances: false
  };

  readonly allTabs: { id: DonneesTabId; label: string }[] = [
    { id: 'general', label: 'Général' },
    { id: 'champs', label: 'Champs' },
    { id: 'affichage', label: 'Affichage' },
    { id: 'regles', label: 'Règles' },
    { id: 'utilisation', label: 'Utilisation' },
    { id: 'import', label: 'Import' },
    { id: 'export', label: 'Export' },
    { id: 'api', label: 'API' },
    { id: 'historique', label: 'Historique' }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dataState: ParametreDonneesStateService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((pm) => {
      this.applyModuleKey(pm);
    });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((qm) => {
      const raw = (qm.get('tab') || 'general').toLowerCase();
      const tab = this.coerceTabId(raw);
      if (!this.isTabVisible(tab)) {
        this.navigateTab('general');
        return;
      }
      this.activeTab = tab;
    });
  }

  private applyModuleKey(pm: ParamMap): void {
    const key = (pm.get('moduleKey') || '').toLowerCase();
    if (!(VALID_MODULE_KEYS as readonly string[]).includes(key)) {
      this.router.navigate(['/main/parametre/donnees/produits'], {
        replaceUrl: true,
        queryParamsHandling: 'merge'
      });
      return;
    }
    this.moduleKey = key;
    this.dbSection = ROUTE_TO_DB_SECTION[key] ?? 'produit';
    this.moduleLabel = this.labelForModule(key);
    this.loadModuleFromApi();
  }

  private loadModuleFromApi(): void {
    this.loadingModule = true;
    this.dataState
      .refreshAll(this.moduleKey)
      .pipe(
        switchMap(() => this.dataState.loadModuleSettingsApi(this.moduleKey)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (g) => {
          if (g) {
            this.general = { ...this.general, ...g };
          }
          this.loadingModule = false;
        },
        error: () => {
          this.loadingModule = false;
          this.toastr.error('Impossible de charger les paramètres du module.', 'Erreur');
        }
      });
  }

  saveGeneral(): void {
    this.dataState.saveModuleSettingsApi(this.moduleKey, this.general).subscribe({
      next: () => this.toastr.success('Paramètres généraux enregistrés.', 'Succès'),
      error: () => {}
    });
  }

  private labelForModule(key: string): string {
    const labels: Record<string, string> = {
      produits: 'Produits',
      clients: 'Clients',
      fournisseurs: 'Fournisseurs',
      'documents-vente': 'Documents vente',
      'documents-achat': 'Documents achat',
      'lignes-document': 'Lignes document',
      promotions: 'Promotions',
      tarifs: 'Tarifs',
      stock: 'Stock',
      entrepots: 'Entrepôts',
      boutiques: 'Boutiques'
    };
    return labels[key] ?? 'Données';
  }

  get visibleTabs(): { id: DonneesTabId; label: string }[] {
    return this.allTabs.filter((t) => this.isTabVisible(t.id));
  }

  isTabVisible(tab: DonneesTabId): boolean {
    if (tab === 'general' || tab === 'historique') {
      return true;
    }
    if (!this.general.champsPersonnalisesActifs && TABS_CUSTOM_DEPENDENT.includes(tab)) {
      return false;
    }
    return true;
  }

  private coerceTabId(raw: string): DonneesTabId {
    const allowed = this.allTabs.map((t) => t.id);
    const hit = allowed.find((a) => a === raw);
    return (hit || 'general') as DonneesTabId;
  }

  selectTab(tab: DonneesTabId): void {
    if (!this.isTabVisible(tab)) {
      return;
    }
    this.navigateTab(tab);
  }

  private navigateTab(tab: DonneesTabId): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: tab === 'general'
    });
  }

  onChampsPersonnalisesChange(): void {
    if (!this.general.champsPersonnalisesActifs && TABS_CUSTOM_DEPENDENT.includes(this.activeTab)) {
      this.navigateTab('general');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
